import type { Op } from './ops';

const key = (tripId: string): string => `trip-explorer:opqueue:${tripId}`;

/** A persistent outbound op queue. Ops emitted while the WebSocket is
 *  disconnected land here (offline-first) and are drained + sent on reconnect.
 *  Ops are idempotent by clock server-side, so re-sending after a flaky
 *  connection is safe. */
export interface OpQueue {
  enqueue(op: Op): void;
  /** Return all queued ops and clear the queue. */
  drain(): Array<Op>;
  peek(): Array<Op>;
  size(): number;
}

export const createOpQueue = (tripId: string, storage: Storage = localStorage): OpQueue => {
  const read = (): Array<Op> => {
    try {
      return JSON.parse(storage.getItem(key(tripId)) ?? '[]') as Array<Op>;
    } catch {
      return [];
    }
  };
  const write = (ops: Array<Op>): void => {
    try {
      storage.setItem(key(tripId), JSON.stringify(ops));
    } catch {
      // storage unavailable (private mode / quota) — drop silently
    }
  };
  return {
    enqueue: (op) => write([...read(), op]),
    drain: () => {
      const ops = read();
      write([]);
      return ops;
    },
    peek: read,
    size: () => read().length,
  };
};
